import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material'
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid'
import EditIcon from '@mui/icons-material/Edit'
import DownloadIcon from '@mui/icons-material/Download'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCareerCategoryStore } from '../../stores/careerCategoryStore'
import { CareerCategoryConfig } from '../../models/CareerCategory'

const editSchema = z.object({
  description: z.string().min(1, '說明不可空白'),
  nursingJobsText: z.string().min(1, '建議護理工作不可空白'),
})

type EditForm = z.infer<typeof editSchema>

export default function CareerCategoryListPage() {
  const { categories, loading, error, fetchCategories, updateCategory, initializeDefaults } =
    useCareerCategoryStore()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<CareerCategoryConfig | null>(null)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })
  const [initializing, setInitializing] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditForm>({
    resolver: zodResolver(editSchema),
    defaultValues: { description: '', nursingJobsText: '' },
  })

  useEffect(() => {
    fetchCategories()
  }, [])

  const hasFirestoreData = categories.some((c) => c.updatedAt !== undefined)

  const handleOpenEdit = (cat: CareerCategoryConfig) => {
    setEditingCategory(cat)
    reset({
      description: cat.description,
      nursingJobsText: cat.nursingJobs.join('\n'),
    })
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingCategory(null)
    reset()
  }

  const onSubmit = async (data: EditForm) => {
    if (!editingCategory) return
    const nursingJobs = data.nursingJobsText
      .split('\n')
      .map((j) => j.trim())
      .filter(Boolean)
    try {
      await updateCategory(editingCategory.number, {
        description: data.description,
        nursingJobs,
      })
      setSnackbar({ open: true, message: `No.${editingCategory.number} 更新成功`, severity: 'success' })
      handleCloseDialog()
    } catch {
      setSnackbar({ open: true, message: '更新失敗，請重試', severity: 'error' })
    }
  }

  const handleInitializeDefaults = async () => {
    setInitializing(true)
    try {
      await initializeDefaults()
      setSnackbar({ open: true, message: '已成功載入 40 筆預設值', severity: 'success' })
    } catch {
      setSnackbar({ open: true, message: '載入預設值失敗，請重試', severity: 'error' })
    } finally {
      setInitializing(false)
    }
  }

  const columns: GridColDef<CareerCategoryConfig>[] = [
    {
      field: 'number',
      headerName: 'No.',
      width: 60,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'categoryLabel',
      headerName: '分類組合',
      width: 300,
    },
    {
      field: 'description',
      headerName: '說明',
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value} placement="top-start">
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              fontSize: 13,
            }}
          >
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'nursingJobs',
      headerName: '建議護理工作',
      width: 280,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
          {(params.value as string[]).slice(0, 3).map((job: string) => (
            <Chip key={job} label={job} size="small" variant="outlined" sx={{ fontSize: 11 }} />
          ))}
          {(params.value as string[]).length > 3 && (
            <Chip label={`+${(params.value as string[]).length - 3}`} size="small" sx={{ fontSize: 11 }} />
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '操作',
      width: 80,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="編輯"
          onClick={() => handleOpenEdit(params.row)}
        />,
      ],
    },
  ]

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            護理職涯分類管理
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            40 種職涯分類的說明與建議工作，可在此即時修改（App 無需重新部署）
          </Typography>
        </Box>
        <Button
          variant={hasFirestoreData ? 'outlined' : 'contained'}
          startIcon={initializing ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={handleInitializeDefaults}
          disabled={initializing || loading}
          color={hasFirestoreData ? 'inherit' : 'primary'}
        >
          {hasFirestoreData ? '重設為預設值' : '載入預設值'}
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* DataGrid */}
      <DataGrid
        rows={categories}
        columns={columns}
        getRowId={(row) => row.number}
        loading={loading}
        rowHeight={56}
        pageSizeOptions={[20, 40]}
        initialState={{ pagination: { paginationModel: { pageSize: 40 } } }}
        disableRowSelectionOnClick
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          編輯分類 No.{editingCategory?.number}
          {editingCategory && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {editingCategory.categoryLabel}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="分類說明"
                  multiline
                  rows={5}
                  fullWidth
                  error={!!errors.description}
                  helperText={errors.description?.message ?? '顯示在 App 綜合總覽的護理職涯分類說明文字'}
                />
              )}
            />
            <Controller
              name="nursingJobsText"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="建議護理工作"
                  multiline
                  rows={4}
                  fullWidth
                  error={!!errors.nursingJobsText}
                  helperText={errors.nursingJobsText?.message ?? '每行輸入一項工作，例如：急診護理'}
                  placeholder={'急診護理\n加護病房護理\n手術室護理'}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
          >
            儲存
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
